import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

/**
 * Global guard: if the current user has profiles.must_change_password = true,
 * force redirect to /wachtwoord-wijzigen until they set a new password.
 * Allowed paths while flag is on: /wachtwoord-wijzigen, /auth, /reset-password.
 */
export function PasswordChangeGate({ children }: { children: React.ReactNode }) {
  const { user, loading, isPasswordRecovery } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (loading || !user || isPasswordRecovery) { setChecked(true); return; }

    // Admin impersonation bypass: when an admin opens a company account via a
    // one-time magic link, the URL carries ?admin_setup=1. Persist a per-tab
    // skip flag so the admin can configure the account without being forced
    // through the password-change screen. The real company user's own login
    // never has this param, so they still get the forced flow.
    try {
      const params = new URLSearchParams(location.search);
      if (params.get("admin_setup") === "1") {
        sessionStorage.setItem(`skip_pw_change_${user.id}`, "1");
      }
    } catch { /* ignore */ }
    const skip = (() => {
      try { return sessionStorage.getItem(`skip_pw_change_${user.id}`) === "1"; }
      catch { return false; }
    })();
    if (skip) { setChecked(true); return; }

    (async () => {
      const { data } = await supabase
        .from("profiles")
        .select("must_change_password")
        .eq("id", user.id)
        .maybeSingle();
      if (cancelled) return;
      const mustChange = !!data?.must_change_password;
      const allowed = ["/wachtwoord-wijzigen", "/auth", "/reset-password"];
      if (mustChange && !allowed.some((p) => location.pathname.startsWith(p))) {
        navigate("/wachtwoord-wijzigen", { replace: true });
      }
      setChecked(true);
    })();
    return () => { cancelled = true; };
  }, [user, loading, isPasswordRecovery, location.pathname, location.search, navigate]);


  if (loading || !checked) return <>{children}</>;
  return <>{children}</>;
}
